// src/utils/formatters.js

/**
 * Formate un prix en FCFA avec espaces (ex: 3 400 FCFA)
 * @param {number} price - Le prix à formater
 * @returns {string} - Le prix formaté
 */
export const formatPriceFCFA = (price) => {
  if (!price || isNaN(price)) return '0 FCFA';
  
  // Utilise l'espace insécable (\u00A0) pour le séparateur de milliers
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true
  }).format(price);
  
  return `${formatted} FCFA`;
};

/**
 * Formate un prix sans devise (juste le nombre avec espaces)
 * @param {number} price - Le prix à formater
 * @returns {string} - Le prix formaté (ex: "3 400")
 */
export const formatPrice = (price) => {
  if (!price || isNaN(price)) return '0';
  
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true
  }).format(price);
};

/**
 * Formate une durée en minutes vers h/min lisible
 * Règle : Si >= 60min → format heure (1h30), sinon → minutes (30min)
 * @param {number} duration - Durée en minutes
 * @returns {string} - Durée formatée (ex: "1h30", "30min", "2h")
 */
export const formatDuration = (duration) => {
  if (!duration || isNaN(duration) || duration <= 0) return '0min';
  
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  
  // Si >= 1 heure, format heure
  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}h${minutes < 10 ? '0' : ''}${minutes}`;
    } else {
      return `${hours}h`;
    }
  } 
  // Sinon, format minutes
  else {
    return `${minutes}min`;
  }
};

/**
 * Formate une durée en version longue (ex: "1 heure 30 minutes")
 * @param {number} duration - Durée en minutes
 * @returns {string} - Durée formatée en toutes lettres
 */
export const formatDurationLong = (duration) => {
  if (!duration || isNaN(duration) || duration <= 0) return '0 minute';
  
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  
  let result = '';
  
  if (hours > 0) {
    result += `${hours} heure${hours > 1 ? 's' : ''}`;
  }
  
  if (minutes > 0) {
    if (hours > 0) result += ' ';
    result += `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  
  return result;
};

/**
 * Formate un numéro de téléphone
 * @param {string} phone - Numéro de téléphone
 * @returns {string} - Téléphone formaté
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  // Retire tous les espaces/tirets
  const cleaned = phone.replace(/[\s\-]/g, '');
  
  // Format par groupes de 2 (ex: 77 123 45 67)
  if (cleaned.length === 9 || cleaned.length === 10) {
    return cleaned.match(/.{1,2}/g)?.join(' ') || cleaned;
  }
  
  return phone;
};